import { crmTaskErrorText } from 'helper/crmTaskErrors';
import useText from 'hooks/useText';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

/**
 * The comments of something (a task) and what sends one. `load()` resolves
 * the list, `add(body, mentions)` the saved comment; the thread toasts who
 * was named. `people` is the team, to name the mentioned ones.
 */
const useCommentThread = ({ key, load, add, people, onCommented }) => {
  const { text, plural } = useText('comments');
  const { text: tasksText } = useText('tasks');
  const [comments, setComments] = useState(null);
  const showError = useCallback(
    (error) => {
      const message = crmTaskErrorText(error, tasksText);
      if (message) toast.error(message);
    },
    [tasksText],
  );
  // Read through a ref so that the load effect keeps depending on `key` only.
  const showErrorRef = useRef(showError);
  showErrorRef.current = showError;
  // The thread reloads when it shows something else (`key`), not each time
  // the caller draws a new `load`.
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    if (!key) return undefined;
    let alive = true;
    setComments(null);
    loadRef.current().then(
      (list) => alive && setComments(list),
      (error) => {
        if (!alive) return;
        showErrorRef.current(error);
        setComments([]);
      },
    );
    return () => {
      alive = false;
    };
  }, [key]);

  const send = useCallback(
    async (body, mentions) => {
      try {
        const comment = await add(body, mentions);
        setComments((list) => [...(list ?? []), comment]);
        const names = people
          .filter((person) => mentions.includes(person.id))
          .map((person) => `${person.firstname} ${person.lastname}`.trim());
        toast.success(names.length ? plural('sentWithMentions', names.length, { names: names.join(', ') }) : text('added'));
        onCommented?.();
        return true;
      } catch (error) {
        showError(error);
        return false;
      }
    },
    [add, people, onCommented, text, plural, showError],
  );

  return { comments, send };
};

export default useCommentThread;
